import type { ResourceArticle } from "../types";

// Public editorial adaptation of docs/marketing/Competitor_Research_Pack/12_AI_NATIVE_VALUE_AND_HUMAN_CONTROL.md
export const article12: ResourceArticle = {
  "slug": "ai-native-value-and-human-control",
  "title": "AI-native value, with humans in control",
  "description": "Explore Simino Social’s AI product direction: brand context, campaign assistance, human approval, quality practices, and transparent value for customers.",
  "category": "Product direction",
  "subject": "AI & human control",
  "focus": "Less repetitive work. Clear human authority.",
  "summary": "Explore how AI can reduce repetitive work while people retain authority over brand, strategy, approvals, and sensitive decisions.",
  "researchDate": "2026-09-20",
  "sourceFile": "12_AI_NATIVE_VALUE_AND_HUMAN_CONTROL.md",
  "sections": [
    {
      "id": "the-customer-value-of-ai-assistance",
      "heading": "The customer value of AI assistance",
      "blocks": [
        {
          "type": "paragraph",
          "text": "An AI-native company should not simply attach a chatbot to an old workflow. It should redesign how customer work gets completed and how the company builds, tests, supports, and improves the product."
        },
        {
          "type": "paragraph",
          "text": "For Simino Social, the promise is:"
        },
        {
          "type": "quote",
          "text": "AI reduces repetitive work and shortens the path from a business goal to a ready-to-publish campaign. People retain authority over brand, strategy, approvals, exceptions, and sensitive decisions."
        },
        {
          "type": "paragraph",
          "text": "The customer benefit should appear in four places:"
        },
        {
          "type": "list",
          "ordered": true,
          "items": [
            "Less manual social-media work",
            "Faster product improvement",
            "More useful capability at an SMB-accessible price",
            "Better consistency without surrendering control"
          ]
        },
        {
          "type": "paragraph",
          "text": "This is **AI-assisted**, not “a company run completely by AI.”"
        }
      ]
    },
    {
      "id": "what-ai-native-should-mean",
      "heading": "What “AI-native” should mean",
      "blocks": [
        {
          "type": "paragraph",
          "text": "AI-native is an operating model, not a marketing adjective."
        },
        {
          "type": "table",
          "headers": [
            "Layer",
            "Conventional pattern",
            "Simino Social target pattern"
          ],
          "rows": [
            [
              "Customer input",
              "User configures many screens",
              "User states a goal and constraints"
            ],
            [
              "Planning",
              "User builds a calendar manually",
              "AI proposes a plan using brand memory"
            ],
            [
              "Creation",
              "Separate writing/design steps",
              "AI prepares channel-specific drafts and assets"
            ],
            [
              "Control",
              "User either does everything or enables broad automation",
              "Assist, Copilot, and controlled Autopilot modes"
            ],
            [
              "Publishing",
              "Calendar triggers posts",
              "Durable jobs, provider validation, retries, and approval gates"
            ],
            [
              "Analytics",
              "Charts and exports",
              "Explanation, recommended next action, and a learning loop"
            ],
            [
              "Product development",
              "Mostly sequential specialist handoffs",
              "AI accelerates research, design, implementation, testing, and documentation while people own decisions"
            ]
          ]
        }
      ]
    },
    {
      "id": "three-proposed-modes-of-human-control",
      "heading": "Three proposed modes of human control",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Simino Social should use three understandable operating modes:"
        },
        {
          "type": "heading",
          "text": "Assist"
        },
        {
          "type": "paragraph",
          "text": "AI recommends ideas, copy, timing, and improvements. The person performs the action."
        },
        {
          "type": "heading",
          "text": "Copilot"
        },
        {
          "type": "paragraph",
          "text": "AI prepares the campaign. A person reviews and approves before publishing. This should be the safest default for new users."
        },
        {
          "type": "heading",
          "text": "Controlled Autopilot"
        },
        {
          "type": "paragraph",
          "text": "AI executes only inside explicit rules: approved brands, networks, content categories, schedules, claims, budgets, and escalation policies. Sensitive or unusual content returns to a human."
        },
        {
          "type": "paragraph",
          "text": "The system should make AI work visible with states such as **Creating**, **Waiting for approval**, **Scheduled**, **Publishing**, **Completed**, and **Needs attention**."
        }
      ]
    },
    {
      "id": "how-ai-can-reduce-repetitive-operating-work",
      "heading": "How AI can reduce repetitive operating work",
      "blocks": [
        {
          "type": "heading",
          "text": "1. Product discovery and market research"
        },
        {
          "type": "paragraph",
          "text": "AI can summarize competitor changes, cluster support requests, draft hypotheses, and compare feature definitions. A human decides whether the source is reliable and whether the insight fits the product strategy."
        },
        {
          "type": "paragraph",
          "text": "**Value passed to customers:** faster response to real needs and less money spent on low-value research administration."
        },
        {
          "type": "heading",
          "text": "2. Product and visual design"
        },
        {
          "type": "paragraph",
          "text": "AI can create alternative information architectures, content hierarchies, wireframe directions, UX copy, accessibility checklists, responsive states, and design-system variants. Designers or product owners select and refine the result."
        },
        {
          "type": "paragraph",
          "text": "**Value passed to customers:** more design exploration before code is committed, fewer avoidable redesign loops, and a simpler experience."
        },
        {
          "type": "heading",
          "text": "3. Coding and refactoring"
        },
        {
          "type": "paragraph",
          "text": "Repository-aware coding agents can help inspect existing code, scaffold well-defined changes, generate migrations, explain unfamiliar modules, and draft documentation. They must follow protected paths, architecture conventions, tenancy boundaries, and security rules."
        },
        {
          "type": "paragraph",
          "text": "**Value passed to customers:** shorter lead time for small, well-scoped improvements and bug fixes."
        },
        {
          "type": "heading",
          "text": "4. Testing and quality engineering"
        },
        {
          "type": "paragraph",
          "text": "AI can propose risk-based test cases, generate unit and integration-test scaffolds, create test data, inspect logs, identify missing edge cases, and help maintain regression suites. Deterministic automation—not an AI opinion—decides whether a build passes."
        },
        {
          "type": "paragraph",
          "text": "**Value passed to customers:** broader repeatable testing without making every additional test linearly increase labor cost."
        },
        {
          "type": "heading",
          "text": "5. Documentation and support assistance"
        },
        {
          "type": "paragraph",
          "text": "AI can draft release notes, explain errors in plain language, route support issues, surface known solutions, and identify documentation gaps. Humans handle exceptions, billing disputes, policy-sensitive cases, and relationship-sensitive support."
        },
        {
          "type": "paragraph",
          "text": "**Value passed to customers:** faster explanations and more consistent self-service while preserving human escalation."
        }
      ]
    },
    {
      "id": "ai-does-not-automatically-improve-software-economics",
      "heading": "AI does not automatically improve software economics",
      "blocks": [
        {
          "type": "paragraph",
          "text": "The evidence is mixed, which is why Simino Social needs measurement and quality gates."
        },
        {
          "type": "paragraph",
          "text": "Google Cloud’s 2024 DORA research found that AI adoption was associated with higher individual productivity, flow, and satisfaction, but also with worse delivery stability and throughput unless teams maintained fundamentals such as small batches and robust testing. A 2025 METR randomized trial found experienced open-source developers working in familiar, mature repositories took 19% longer with the tested early-2025 AI tools. METR explicitly warns against generalizing that result to every developer or task."
        },
        {
          "type": "paragraph",
          "text": "The conclusion is not “AI always makes development faster.” It is:"
        },
        {
          "type": "quote",
          "text": "Use AI where measured results improve; keep architecture, security, testing, review, and customer outcomes as the acceptance criteria."
        }
      ]
    },
    {
      "id": "quality-system-faster-without-compromising-quality",
      "heading": "Quality system: faster without compromising quality",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Every AI-assisted change should pass the same or stronger release controls:"
        },
        {
          "type": "list",
          "ordered": true,
          "items": [
            "**Evidence-first discovery:** inspect the existing repository, design system, APIs, data model, and tests before changing code.",
            "**Explicit acceptance criteria:** define expected behavior, error states, permissions, accessibility, analytics, and performance.",
            "**Small changes:** use narrow pull requests and reversible migrations.",
            "**Human review:** require a competent owner for architecture, security-sensitive behavior, billing, privacy, and provider permissions.",
            "**Automated checks:** formatting, linting, type checks, unit tests, integration tests, dependency checks, and secret scanning.",
            "**Authorization and tenancy tests:** verify workspace isolation, server-side permissions, role boundaries, and agency access.",
            "**Publishing reliability tests:** verify time zones, retries, idempotency, partial failures, provider limits, and duplicate prevention.",
            "**E2E and visual regression:** test the important customer journeys and compare critical UI states across screen sizes.",
            "**Human UAT:** validate real workflows, copy, brand behavior, and edge cases.",
            "**Staged release:** use feature flags, observability, rollback, and post-release monitoring."
          ]
        },
        {
          "type": "paragraph",
          "text": "NIST’s Generative AI Profile recommends treating generative-AI risk as a lifecycle responsibility. For Simino Social, that means AI output review, provenance where appropriate, privacy controls, monitoring, incident handling, and clear user authority—not a one-time model selection."
        }
      ]
    },
    {
      "id": "how-cost-savings-should-reach-the-customer",
      "heading": "How cost savings should reach the customer",
      "blocks": [
        {
          "type": "paragraph",
          "text": "AI-native efficiency should be allocated deliberately rather than turned into a vague “low price” claim."
        },
        {
          "type": "heading",
          "text": "Pass through directly"
        },
        {
          "type": "list",
          "ordered": false,
          "items": [
            "Lower entry pricing for solo businesses and creators",
            "Practical multi-network allowances",
            "Useful approval and brand-memory features without enterprise-only packaging",
            "Reduced setup effort through guided onboarding and reusable context",
            "Faster routine improvements and fixes"
          ]
        },
        {
          "type": "heading",
          "text": "Reinvest for quality"
        },
        {
          "type": "list",
          "ordered": false,
          "items": [
            "Security review and privacy controls",
            "Reliable queues, monitoring, backups, and provider-failure handling",
            "Automated and human testing",
            "Customer support and documentation",
            "API compliance and app-review work"
          ]
        },
        {
          "type": "heading",
          "text": "Price expensive consumption honestly"
        },
        {
          "type": "paragraph",
          "text": "Generative images, long video processing, large-scale listening, premium models, and high-volume publishing have real marginal cost. Use understandable allowances, model routing, caching, batching, and optional usage packs. Avoid an “unlimited AI” promise that later requires aggressive throttling."
        },
        {
          "type": "paragraph",
          "text": "The right customer promise is:"
        },
        {
          "type": "quote",
          "text": "We use AI efficiency to deliver more useful work per dollar, while continuing to invest in reliability, security, and human control."
        }
      ]
    },
    {
      "id": "why-a-native-product-can-be-better-than-an-ai-chatbot-with-a-connection",
      "heading": "Why a native product can be better than an AI chatbot with a connection",
      "blocks": [
        {
          "type": "paragraph",
          "text": "A general AI chatbot connected to a scheduler can be excellent for ad hoc requests. It may be the fastest place to brainstorm a campaign or issue a simple command. Simino Social should not deny that value; it can support chatbot access as an additional interface."
        },
        {
          "type": "paragraph",
          "text": "However, a connected chatbot is usually an interface to the operational system, not a replacement for it."
        },
        {
          "type": "table",
          "headers": [
            "Need",
            "General chatbot + connector",
            "Simino Social target experience"
          ],
          "rows": [
            [
              "Business context",
              "Often supplied in conversation or external context",
              "Persistent workspace-level brand memory and rules"
            ],
            [
              "Planning",
              "Flexible one-off conversation",
              "Repeatable goal-to-campaign workflow"
            ],
            [
              "Approvals",
              "Must be expressed through commands and supported connector actions",
              "First-class roles, review states, approvals, and audit trail"
            ],
            [
              "Publishing reliability",
              "Depends on connector action and downstream tool",
              "Durable scheduling, retries, idempotency, time zones, and provider status"
            ],
            [
              "Multi-user operations",
              "Conversation is person-centric",
              "Workspace, business, client, agency, role, and approver model"
            ],
            [
              "Exceptions",
              "Chatbot explains or retries if asked",
              "Operational queues, “needs attention,” partial-failure handling, and recovery"
            ],
            [
              "Brand safety",
              "Prompt-level instructions",
              "Stored approved claims, restricted claims, tone, assets, and escalation rules"
            ],
            [
              "Learning loop",
              "Broad conversational reasoning",
              "Campaign results tied to future recommendations and the same brand context"
            ],
            [
              "Cost visibility",
              "Chat subscription plus connected product and possible usage",
              "Product plan designed around the complete workflow"
            ]
          ]
        }
      ]
    },
    {
      "id": "product-and-pricing-principles",
      "heading": "Product and pricing principles",
      "blocks": [
        {
          "type": "list",
          "ordered": true,
          "items": [
            "Optimize for completed campaigns, not AI messages consumed.",
            "Keep the common SMB workflow simple and affordable.",
            "Charge more when real cost or organizational complexity rises: more brands, heavy media generation, advanced listening, governance, or white labeling.",
            "Never turn safety controls into friction purely to create an upsell.",
            "Publish clear definitions for users, social profiles, brands, posts, and AI allowances.",
            "Measure time saved, approval rate, publish success, correction rate, support burden, and customer outcome—not only feature usage."
          ]
        }
      ]
    },
    {
      "id": "sources",
      "heading": "Sources",
      "blocks": [
        {
          "type": "list",
          "ordered": false,
          "items": [
            "[DORA Research: 2024](https://dora.dev/research/2024/dora-report/)",
            "[METR: Measuring the Impact of Early-2025 AI on Experienced Open-Source Developer Productivity](https://metr.org/blog/2025-07-10-early-2025-ai-experienced-os-dev-study/)",
            "[NIST AI Risk Management Framework: Generative AI Profile](https://www.nist.gov/publications/artificial-intelligence-risk-management-framework-generative-artificial-intelligence)",
            "[Buffer pricing and supported integrations](https://buffer.com/pricing)"
          ]
        }
      ]
    }
  ]
};
